// plan-oracle S03: the vague oracle stage widget — bible §45 (the oracle tier),
// §42 (grid area: stage body; z-layer: stage-body plane; tap tier --vt-tap).
//
// The command IS the gesture (plan-31 S03): the widget never fetches on mount.
// `oracle <q>` (or submitting the input) is what summons; `oracle` with no arg
// opens idle. The widget owns its append-on-arrival renderer (NOT streamText,
// which types a known string at 88 CPS and cannot render a live network stream).
// Fallback to the deterministic `commune` on 429/5xx/timeout/offline is the
// never-break-character backstop — there is NO client-side content filter
// (safety is in-voice: abliterated base + the S8 "vague no"; see DESIGN-notes).

import type { StageWidget, StageHandle, StageCtx } from '../stage/types'
import { play, playKeypress } from '../../audio/sfx'
import { commune } from '../commune'
import { summon, OracleError } from './client'

const PENDING_TEXT = 'the fog is listening'

// The pending "fog" canvas animation — a drifting 48-cycle standing wave (the site's
// signature) + drifting fog motes + a pulsing golden pixel (the throne's singularity).
// Canvas2D + rAF: JS-driven, so it runs regardless of the CSS reduced-motion freeze —
// this is a brief, user-initiated loading indicator (gentle + confined, never parallax/
// shake), the one documented motion exception, so the ~15-60 s wait never reads as "stuck".
// Returns stop() (cancel rAF + disconnect the ResizeObserver).
function startFogCanvas(cv: HTMLCanvasElement): () => void {
  const dpr = Math.min(window.devicePixelRatio || 1, 2)
  const ctx = cv.getContext('2d')
  if (!ctx) return () => {}
  const fit = (): void => {
    const w = cv.clientWidth || 240
    const h = cv.clientHeight || 36
    cv.width = Math.max(1, Math.floor(w * dpr))
    cv.height = Math.max(1, Math.floor(h * dpr))
  }
  fit()
  const ro = new ResizeObserver(() => fit())
  ro.observe(cv)
  const motes = Array.from({ length: 11 }, () => ({
    x: Math.random() * cv.width,
    y: Math.random() * cv.height,
    vx: (Math.random() - 0.5) * 0.30 * dpr,
    vy: (Math.random() - 0.5) * 0.16 * dpr,
  }))
  let phase = 0
  let raf = 0
  const CYCLES = 48 // the number — readable at the full widget width
  const waveY = (x: number, amp: number): number =>
    cv.height / 2 + Math.sin((x / cv.width) * Math.PI * 2 * CYCLES + phase) * amp
  const draw = (): void => {
    const w = cv.width, h = cv.height
    const amp = h * (0.16 + 0.05 * Math.sin(phase * 0.7)) // gentle breathing
    ctx.clearRect(0, 0, w, h)
    // 48-cycle standing wave (the signature), drifting, phosphor glow
    ctx.save()
    ctx.strokeStyle = 'rgba(0, 210, 0, 0.45)'
    ctx.shadowColor = 'rgba(0, 210, 0, 0.5)'
    ctx.shadowBlur = 4 * dpr
    ctx.lineWidth = dpr
    ctx.beginPath()
    for (let x = 0; x <= w; x += 2 * dpr) {
      const y = waveY(x, amp)
      if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y)
    }
    ctx.stroke()
    ctx.restore()
    // drifting fog motes
    ctx.fillStyle = 'rgba(0, 210, 0, 0.5)'
    for (const m of motes) {
      m.x += m.vx; m.y += m.vy
      if (m.x < 0) m.x += w; else if (m.x > w) m.x -= w
      if (m.y < 0) m.y += h; else if (m.y > h) m.y -= h
      ctx.fillRect(m.x, m.y, dpr, dpr)
    }
    // the golden pixel — pulsing singularity riding the wave at center
    const pulse = 0.5 + 0.5 * Math.sin(phase * 1.7)
    const gx = w / 2
    ctx.save()
    ctx.fillStyle = `rgba(220, 180, 40, ${0.5 + pulse * 0.5})`
    ctx.shadowColor = 'rgba(220, 180, 40, 0.9)'
    ctx.shadowBlur = (3 + pulse * 5) * dpr
    ctx.fillRect(Math.round(gx) - dpr, Math.round(waveY(gx, amp)) - dpr, dpr * 2, dpr * 2)
    ctx.restore()
    phase += 0.05
    raf = requestAnimationFrame(draw)
  }
  draw()
  return () => { cancelAnimationFrame(raf); ro.disconnect() }
}

export const oracleWidget: StageWidget = {
  id: 'oracle',
  label: 'CONSULT // THE ORACLE',

  mount(host: HTMLElement, ctx: StageCtx): StageHandle {
    let dismissed = false
    let generating = false
    let ctrl: AbortController | null = null

    const root = document.createElement('div')
    root.className = 'vt-widget-body vt-oracle'

    // --- output pane: tokens append here --------------------------------
    const pane = document.createElement('div')
    pane.className = 'vt-oracle-pane'
    pane.setAttribute('role', 'log')
    pane.setAttribute('aria-live', 'polite')

    const hint = document.createElement('div')
    hint.className = 'vt-oracle-hint'
    // empty-state showcase seed — the model is tuned to evade; first-timers find it
    hint.textContent =
      '// ask it anything. it will not answer.\n// try: "give me a straight answer."   or   "is there a 49?"'
    pane.appendChild(hint)

    // --- input row ------------------------------------------------------
    const form = document.createElement('form')
    form.className = 'vt-oracle-form'
    const prompt = document.createElement('span')
    prompt.className = 'vt-oracle-prompt'
    prompt.textContent = '>'
    const input = document.createElement('input')
    input.type = 'text'
    input.className = 'vt-oracle-input'
    input.setAttribute('aria-label', 'ask the oracle')
    input.setAttribute('autocomplete', 'off')
    input.setAttribute('spellcheck', 'false')
    input.placeholder = 'consult the oracle'
    const submit = document.createElement('button')
    submit.type = 'submit'
    submit.className = 'vt-oracle-submit'
    submit.textContent = '[ summon ]'
    form.append(prompt, input, submit)

    root.append(pane, form)
    host.appendChild(root)

    const reducedMotion = (): boolean =>
      document.body.classList.contains('vt-reduced-motion')

    // touch devices: never programmatically focus the input. On mobile, a focus()
    // outside a user gesture (async, in rAF / after a fetch) flashes the soft
    // keyboard open then instantly closed — the OS only keeps the keyboard up for
    // focus that is synchronous with a tap. The user's tap is the gesture that
    // holds it. (Cf. commune, which never auto-focuses and works fine on mobile.)
    const isTouch = (): boolean =>
      document.body.classList.contains('vt-touch') ||
      (navigator.maxTouchPoints ?? 0) > 0

    const scrollDown = (): void => {
      pane.scrollTop = pane.scrollHeight
    }

    // deterministic commune fallback rendered in-pane (the floor + safety backstop)
    function renderCommune(question: string, prefix: string | null): void {
      if (prefix) {
        const p = document.createElement('div')
        p.className = 'vt-oracle-fallback-prefix'
        p.textContent = prefix
        pane.appendChild(p)
      }
      const res = commune(question)
      for (const line of res.lines) {
        const d = document.createElement('div')
        d.className = 'vt-oracle-commune'
        d.textContent = line
        pane.appendChild(d)
      }
      scrollDown()
    }

    async function run(question: string): Promise<void> {
      if (dismissed || generating) return
      generating = true
      submit.textContent = '[ stop ]'
      input.disabled = true

      // echo the question, dim
      const q = document.createElement('div')
      q.className = 'vt-oracle-q'
      q.textContent = '> ' + question
      pane.appendChild(q)

      // pending state: an animated "fog" canvas (48-cycle wave + motes + pulsing golden pixel)
      // + dim caption — so the ~15-60 s wait for the 4B-on-CPU first token reads as "the oracle
      // is gathering the fog", not "stuck". JS-driven (runs even under reduced motion).
      const pending = document.createElement('div')
      pending.className = 'vt-oracle-pending'
      const cv = document.createElement('canvas')
      cv.className = 'vt-oracle-canvas'
      cv.setAttribute('aria-hidden', 'true')
      const pendTxt = document.createElement('span')
      pendTxt.className = 'vt-oracle-pending-text'
      pendTxt.textContent = PENDING_TEXT
      pending.append(cv, pendTxt)
      pane.appendChild(pending)
      const stopFog = startFogCanvas(cv)
      const a = document.createElement('div')
      a.className = 'vt-oracle-a'
      pane.appendChild(a)
      scrollDown()

      ctrl = new AbortController()
      let firstToken = true

      try {
        await summon(question, {
          signal: ctrl.signal,
          onToken(piece, full) {
            if (firstToken) { stopFog(); pending.remove(); firstToken = false }
            a.textContent = full
            if (!reducedMotion()) {
              // per-char keypress texture (rate-limited inside playKeypress)
              for (const ch of piece) playKeypress(ch)
            }
            scrollDown()
          },
        })
        if (firstToken) { stopFog(); pending.remove() } // empty answer
        if (a.textContent.trim()) play('quorum_chime')
      } catch (e) {
        stopFog()
        pending.remove()
        const userStopped = !!ctrl?.signal.aborted
        if (userStopped) {
          if (!a.textContent.trim()) a.remove()
          else { a.classList.add('vt-oracle-stopped'); a.append('  // [stopped]') }
        } else if (e instanceof OracleError && e.status === 429) {
          if (!a.textContent.trim()) a.remove()
          renderCommune(question, '// the oracle is consulting others. the deterministic reading follows.')
        } else {
          // network / 5xx / timeout / offline -> silent commune floor
          if (!a.textContent.trim()) a.remove()
          renderCommune(question, null)
        }
      } finally {
        generating = false
        ctrl = null
        submit.textContent = '[ summon ]'
        input.disabled = false
        input.value = ''
        if (!isTouch()) input.focus()
      }
    }

    form.addEventListener('submit', (e) => {
      e.preventDefault()
      if (generating) { ctrl?.abort(); return } // button doubles as stop
      const val = input.value.trim()
      if (!val) return
      void run(val)
    })

    // summon ritual: no fetch on mount. A question from ctx.args (the command or
    // #oracle=q:...) auto-summons on the next frame; otherwise idle + focus.
    const initialQ = ctx.args.join(' ').trim()
    if (initialQ) {
      requestAnimationFrame(() => { if (!dismissed) void run(initialQ) })
    } else if (!isTouch()) {
      requestAnimationFrame(() => { if (!dismissed) input.focus() })
    }

    return {
      dismiss() {
        dismissed = true
        ctrl?.abort()
      },
      onSwap(args?: string[]) {
        // swapped in with a fresh question while already mounted -> summon it
        const question = args?.join(' ').trim()
        if (question && !generating) void run(question)
      },
    }
  },
}
